import React, { useState, useEffect, useRef } from 'react';
import { Linkedin, Mail, Users, Award, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

/**
 * Animated Counter Component
 * Triggers animation only when it enters the viewport
 */
const Counter = ({ end, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setHasStarted(true);
      },
      { threshold: 0.1 }
    );

    if (countRef.current) observer.observe(countRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!hasStarted) return;

    let start = 0;
    const increment = end / (duration / 16);
    
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [hasStarted, end, duration]);

  return <span ref={countRef}>{count}</span>;
};

const Team = () => {
  const { isArabic } = useLanguage();

  const team = [
    {
      name: isArabic ? "المنيرهيري، عبد الله فهد" : "Almunayhiri, Abdullah Fahad",
      role: isArabic ? "مجموعة عفام" : "AFAM Group",
      specialty: isArabic ? "المؤسس والرئيس التنفيذي" : "Founder & CEO",
      image: "/a1.jpg",
    }
    // {
    //   name: isArabic ? "عبد القدوس متين منسي" : "Abdul Kuddus Motin Munsi",
    //   role: isArabic ? "مجموعة عفام" : "AFAM Group",
    //   specialty: isArabic ? "المدير العام" : "Sales Manager",
    //   image: "/boss11.jpg",
    // },
    // {
    //   name: isArabic ? "المهندس محمد سجاد حسين" : "Eng. Md Sazzad Hossen",
    //   role: isArabic ? "مجموعة عفام" : "AFAM Group",
    //   specialty: isArabic ? "الهندسة الصناعية" : "Industrial Engineering",
    //   image: "",
    // },
  ];

  return (
    <section id="team" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className={`flex flex-col md:flex-row justify-between items-end mb-16 gap-6 ${isArabic ? 'md:flex-row-reverse' : ''}`}>
          <div className={`max-w-2xl ${isArabic ? 'text-right' : 'text-left'}`}>
            <h2 className="text-3xl md:text-4xl font-bold text-corp-blue mb-4">
              {isArabic ? "فريق القيادة لدينا" : "Our Leadership Team"}
            </h2>
            <div className={`w-20 h-1.5 bg-saudi-green rounded-full mb-6 ${isArabic ? 'ml-auto' : ''}`}></div>
            <p className="text-gray-600 text-lg">
              {isArabic 
                ? "يقودنا خبراء في الصناعة يتمتعون بعقود من الخبرة في عمليات التجزئة والخدمات الغذائية والتوسع السوقي في جميع أنحاء المملكة."
                : "Driven by industry experts with decades of experience in retail operations, food services, and market expansion throughout the Kingdom."}
            </p>
          </div>

          <Link 
            to="/careers" 
            className={`text-saudi-green font-bold hover:underline flex items-center gap-2 transition-all group ${isArabic ? 'flex-row-reverse' : ''}`}
          >
            {isArabic ? "انضم إلى فريقنا" : "Join Our Team"} 
            <span className={`${isArabic ? 'rotate-180' : 'group-hover:translate-x-1'} transition-transform`}>→</span>
          </Link>
        </div>

        {/* Dynamic Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          {/* Authority Stat */}
          <div className="bg-white p-8 rounded-[2.5rem] shadow-sm flex items-center gap-6 border-b-4 border-saudi-green hover:shadow-md transition-shadow">
            <div className="p-4 bg-saudi-green/10 rounded-2xl text-saudi-green">
              <Award size={40} />
            </div>
            <div>
              <div className="text-4xl font-black text-corp-blue">
                <Counter end={26} />+
              </div>
              <p className="text-gray-500 font-medium uppercase tracking-tighter text-sm">
                {isArabic ? "سنوات من الخبرة" : "Years of Experties"}
              </p>
            </div>
          </div>

          {/* Employees Stat */}
          <div className="bg-white p-8 rounded-[2.5rem] shadow-sm flex items-center gap-6 border-b-4 border-corp-blue hover:shadow-md transition-shadow">
            <div className="p-4 bg-corp-blue/10 rounded-2xl text-corp-blue">
              <Users size={40} />
            </div>
            <div>
              <div className="text-4xl font-black text-corp-blue">
                <Counter end={200} />+
              </div>
              <p className="text-gray-500 font-medium uppercase tracking-tighter text-sm">
                {isArabic ? "موظف متميز" : "Expert Employees"}
              </p>
            </div>
          </div>

          {/* Branches Stat */}
          <div className="bg-white p-8 rounded-[2.5rem] shadow-sm flex items-center gap-6 border-b-4 border-saudi-green hover:shadow-md transition-shadow">
            <div className="p-4 bg-saudi-green/10 rounded-2xl text-saudi-green">
              <MapPin size={40} />
            </div>
            <div>
              <div className="text-4xl font-black text-corp-blue">
                <Counter end={12} />+
              </div>
              <p className="text-gray-500 font-medium uppercase tracking-tighter text-sm">
                {isArabic ? "فرعاً نشطاً" : "Active Branches"}
              </p>
            </div>
          </div>
        </div>

        {/* Leadership Team Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {team.map((member, index) => (
            <div key={index} className="bg-white rounded-[2.5rem] overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group border border-slate-100">
              <div className="relative h-80 overflow-hidden">
                <img 
                  src={member.image} 
                  alt={member.name}
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 scale-105 group-hover:scale-100"
                />
                <div className={`absolute bottom-6 ${isArabic ? 'left-6' : 'right-6'} flex flex-col gap-3 transform ${isArabic ? '-translate-x-20' : 'translate-x-20'} group-hover:translate-x-0 transition-transform duration-500`}>
                  <button className="p-3 bg-white/90 backdrop-blur-sm rounded-full text-corp-blue shadow-lg hover:bg-saudi-green hover:text-white transition-all transform hover:scale-110">
                    <Linkedin size={20} />
                  </button>
                  <button className="p-3 bg-white/90 backdrop-blur-sm rounded-full text-corp-blue shadow-lg hover:bg-saudi-green hover:text-white transition-all transform hover:scale-110">
                    <Mail size={20} />
                  </button>
                </div>
              </div>
              
              <div className={`p-8 ${isArabic ? 'text-right' : 'text-left'}`}>
                <p className="text-saudi-green font-bold text-xs uppercase tracking-[0.2em] mb-3">
                  {member.specialty}
                </p>
                <h3 className="text-2xl font-bold text-corp-blue mb-1">{member.name}</h3>
                <p className="text-gray-500 font-medium text-sm italic">{member.role}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Team;
